/**
 * Realtime client.
 *
 * One WebSocket to the gateway, subscribed to this player's room plus the
 * world and market feeds. Events refresh the relevant stores immediately;
 * the existing polling stays as the fallback, so losing the socket degrades
 * to the pre-realtime experience rather than breaking anything.
 *
 * Reconnects supply the last seen sequence so the gap is replayed; a
 * `resync` from the server means the gap was too large, and the client
 * falls back to a full refresh.
 */

import { api } from "./api";
import { useApp } from "./store";

export interface RealtimeEventShape {
  eventId: string;
  sequence: number;
  type: string;
  aggregateId?: string;
  aggregateVersion?: number;
  serverTime: string;
  payload: Record<string, unknown>;
}

type Handler = (event: RealtimeEventShape) => void;

const listeners = new Set<Handler>();

/** Subscribe to every delivered event. Returns the unsubscribe function. */
export function onRealtime(fn: Handler): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

let socket: WebSocket | null = null;
let playerId: string | null = null;
let lastSequence = 0;
let backoffMs = 1000;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let stopped = true;
const extraRooms = new Set<string>();

function baseRooms(): string[] {
  return playerId ? [`player:${playerId}`, "world", "market", ...extraRooms] : [];
}

export function startRealtime(id: string): void {
  playerId = id;
  stopped = false;
  connect();
}

export function stopRealtime(): void {
  stopped = true;
  playerId = null;
  extraRooms.clear();
  if (reconnectTimer) clearTimeout(reconnectTimer);
  socket?.close();
  socket = null;
}

/** Join an additional room (e.g. `chat:<channelId>`) for as long as needed. */
export function joinRoom(room: string): () => void {
  extraRooms.add(room);
  send({ type: "subscribe", rooms: [room] });
  return () => {
    extraRooms.delete(room);
    send({ type: "unsubscribe", rooms: [room] });
  };
}

function send(message: unknown): void {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
}

function connect(): void {
  if (stopped || !playerId) return;
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const ws = new WebSocket(`${proto}://${location.host}/realtime`);
  socket = ws;

  ws.onopen = () => {
    backoffMs = 1000;
    send({
      type: "subscribe",
      rooms: baseRooms(),
      ...(lastSequence > 0 ? { lastSequence } : {}),
    });
  };

  ws.onmessage = (raw) => {
    let msg: { type: string; event?: RealtimeEventShape; sequence?: number };
    try {
      msg = JSON.parse(String(raw.data)) as typeof msg;
    } catch {
      return;
    }
    if (msg.type === "hello" && typeof msg.sequence === "number" && lastSequence === 0) {
      lastSequence = msg.sequence;
      return;
    }
    if (msg.type === "resync") {
      void refreshDashboard();
      return;
    }
    if (msg.type === "event" && msg.event) {
      lastSequence = Math.max(lastSequence, msg.event.sequence);
      handle(msg.event);
      for (const fn of listeners) fn(msg.event);
    }
  };

  ws.onclose = () => {
    if (stopped) return;
    reconnectTimer = setTimeout(connect, backoffMs);
    backoffMs = Math.min(backoffMs * 2, 30_000);
  };
  ws.onerror = () => ws.close();
}

/* ------------------------------------------------------------------ */

let dashboardTimer: ReturnType<typeof setTimeout> | null = null;

/** Coalesce bursts (a settlement tick touches many aggregates) into one call. */
function refreshDashboardSoon(): void {
  if (dashboardTimer) return;
  dashboardTimer = setTimeout(() => {
    dashboardTimer = null;
    void refreshDashboard();
  }, 800);
}

async function refreshDashboard(): Promise<void> {
  try {
    useApp.getState().setDashboard(await api.dashboard());
  } catch {
    // The 20s poll remains the fallback.
  }
}

const PLAYER_EVENT_TYPES = new Set([
  "wallet.changed",
  "unit.leased",
  "unit.vacated",
  "facility.paused",
  "facility.installed",
  "property.offer",
  "parcel.owner_changed",
  "building.constructed",
]);

function handle(event: RealtimeEventShape): void {
  if (PLAYER_EVENT_TYPES.has(event.type)) refreshDashboardSoon();

  // Ownership changed somewhere on the map: refetch the chunk that holds the
  // feature, if it is loaded, so labels and tints update under everyone.
  if (
    (event.type === "parcel.owner_changed" || event.type === "building.constructed") &&
    event.aggregateId
  ) {
    const { chunks, setChunk } = useApp.getState();
    for (const [chunkId, tile] of Object.entries(chunks)) {
      const hit =
        tile.parcels.some((p) => p.id === event.aggregateId) ||
        tile.buildings.some((b) => b.id === event.aggregateId);
      if (!hit) continue;
      api.chunkFeatures(chunkId).then(
        (fresh) => setChunk(chunkId, fresh),
        () => undefined,
      );
      break;
    }
  }
}
